import React from "react";
import moment from "moment";

import connectToStores from "alt-utils/lib/connectToStores";

import config from "../config";
import { formatPercent, formatPrice } from "../util.js";

import { colors } from "material-ui/styles";
import { RaisedButton, Dialog, FlatButton } from "material-ui";
import { Link } from "react-router";

import UserStore from "../stores/UserStore";
import RevenueSharingUtil from "../utils/RevenueSharingUtil";

import WorkingIndicator from "./partials/working-indicator.jsx";
import TableRefreshIndicator from "./partials/table-refresh-indicator.jsx";
import GenericAnalyticsTable from "./partials/generic-analytics-table.jsx";
import MonthPicker from "./partials/month-picker.jsx";
import InfoNotice from "./partials/info-notice.jsx";
import LinkLike from "./partials/link-like.jsx";
import StartRevenueSharingButton from "./partials/start-revenue-sharing-button.jsx";

class RevenueSharing extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isFetching: false,
      isExporting: false,
      showGeneratedExport: null,
      status: null,
      activity: null,
      selectedMonth: MonthPicker.getCurrentMonth()
    };

    this.exportReport = this.exportReport.bind(this);
  }

  static getStores() {
    return [UserStore];
  }

  static getPropsFromStores() {
    return UserStore.getState();
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  exportReport() {
    const url = `${
      config.endpoint
    }revenue_sharing/export_detailed_report?month=${
      this.state.selectedMonth
    }&${UserStore.makeApiKeyQueryString()}`;

    fetch(url)
      .then(r => r.json())
      .then(d => {
        if (d.error || !d.temporary_export) {
          window.alert(
            `Export failed: ${
              d.error ? d.error.message : "An unknown error occurred"
            }`
          );
        } else {
          this.setState({ isExporting: true });
          this._checkTemporaryExport(d.temporary_export, 500);
        }
      });
  }

  _checkTemporaryExport(id, wait) {
    setTimeout(() => {
      fetch(
        `${
          config.endpoint
        }temporary_exports/${id}?${UserStore.makeApiKeyQueryString()}`
      )
        .then(r => r.json())
        .then(e => {
          if (e.completed_at) {
            this.setState({ isExporting: false });
            if (e.download_url) {
              this.setState({ showGeneratedExport: e });
            } else {
              window.alert(`Export failed: ${e.error}`);
            }
          } else {
            this._checkTemporaryExport(id, Math.min(4000, wait * 2)); // backoff: 500ms, 1s, 2s, 4s max wait
          }
        });
    }, wait);
  }

  _fetchActivity() {
    this.setState({ isFetchingActivity: true });
    RevenueSharingUtil.fetchRevenueSharingActivity(
      UserStore.getState().apiKey,
      { month: this.state.selectedMonth }
    )
      .then(data =>
        this.setState({ isFetchingActivity: false, activity: data })
      )
      .catch(e =>
        window.alert(`Failed to load Revenue Sharing activity: ${e.message}`)
      );
  }

  componentWillReceiveProps() {
    if (!this.state.status && !this.state.isFetching) {
      this.setState({ isFetching: true });
      RevenueSharingUtil.fetchRevenueSharingStatus(UserStore.getState().apiKey)
        .then(data => this.setState({ isFetching: false, status: data }))
        .catch(e =>
          window.alert(`Failed to load Revenue Sharing: ${e.message}`)
        );
      this._fetchActivity();
    }
  }

  componentDidMount() {
    this.componentWillReceiveProps();
  }

  render() {
    if (!this.state.status || this.state.isFetching) {
      return (
        <WorkingIndicator fullPage={true} style={{ margin: "10% auto" }} />
      );
    }

    const receivingDetails = this.state.status.details;

    const receivingAccount =
      receivingDetails && receivingDetails.external_accounts.data[0];

    const isCurrentMonth =
      this.state.selectedMonth === MonthPicker.getCurrentMonth();

    const makeStripeDestination = country =>
      `${
        config.endpoint
      }revenue_sharing/stripe_redirect?redirect_url=${encodeURIComponent(
        window.location.href
      )}&${UserStore.makeApiKeyQueryString()}&type=${
        this.state.status.is_ready ? "management" : "onboarding"
      }${country ? `&country=${country}` : ""}`;

    return (
      <div style={{ fontSize: "12pt", maxWidth: 700, margin: "2vw auto 0" }}>
        {this.state.isExporting || this.state.showGeneratedExport ? (
          <Dialog
            title={
              this.state.showGeneratedExport
                ? "Revenue Sharing itemization"
                : "Exporting Revenue Sharing itemization..."
            }
            modal={true}
            openImmediately={true}
            open={true}
            actions={
              this.state.showGeneratedExport
                ? [
                    <FlatButton
                      label="Close"
                      onTouchTap={() =>
                        this.setState({ showGeneratedExport: null })
                      }
                    />
                  ]
                : []
            }
          >
            {this.state.showGeneratedExport ? (
              <div>
                <p>Great news, your revenue sharing itemization is ready!</p>
                <p>
                  <strong>{this.state.showGeneratedExport.description}</strong>
                  <br />
                  <a
                    target="_blank"
                    href={this.state.showGeneratedExport.download_url}
                  >
                    <strong>Click here to download.</strong>
                  </a>
                </p>
              </div>
            ) : (
              <div>
                <WorkingIndicator centered />
                <p>
                  Your report is now being generated! This may take anywhere
                  from a few seconds to a few minutes. Keep this page open and
                  it will automatically update when your report is ready.
                </p>
                <p>
                  Feel free to close this page. You will receive an email with a
                  download link as soon as your report has been generated.
                </p>
              </div>
            )}
          </Dialog>
        ) : null}
        <h2>Eventive Revenue Sharing</h2>
        <p>
          Use Eventive Revenue Sharing to easily share screening proceeds with
          multiple parties. Eventive automates every step of the
          process&mdash;from setup all the way to payouts.
          <InfoNotice style={{ marginTop: 10 }}>
            Questions? Check out our{" "}
            <a
              href="https://help.eventive.org/en/articles/5043252-eventive-revenue-sharing"
              target="_blank"
            >
              guide to Eventive Revenue Sharing
            </a>
            .
          </InfoNotice>
        </p>
        <div
          style={{
            margin: "30px -20px 0",
            backgroundColor: colors.blue50,
            border: `1px solid ${colors.blue200}`,
            borderRadius: 20,
            padding: "20px 20px 10px"
          }}
        >
          <h3>Your Revenue Sharing Account</h3>
          <p>
            Eventive Revenue Sharing is powered by{" "}
            <a href="https://stripe.com/" target="_blank">
              Stripe Express
            </a>
            . We'll securely transfer all your earnings directly to a bank
            account or debit card of your choice on a monthly basis.
          </p>
          <p>
            Status:{" "}
            <strong>
              {this.state.status.is_ready ? (
                <span>
                  <span style={{ color: "green" }}>You're all set!</span> You
                  will receive a single combined{" "}
                  {receivingDetails
                    ? receivingDetails.default_currency.toUpperCase()
                    : ""}{" "}
                  payout every month to{" "}
                  {receivingAccount
                    ? `your ${
                        receivingAccount.bank_name || receivingAccount.brand
                      } account ending in ${receivingAccount.last4}`
                    : "your bank"}
                  .
                </span>
              ) : (
                <span style={{ color: "red" }}>
                  Not yet connected. You will not receive payouts.
                </span>
              )}
            </strong>
          </p>
          {(UserStore.getUser() || {}).type === "festival_organizer" ||
          this.state.status.is_ready ||
          (this.state.activity &&
            (this.state.activity.current_events.length ||
              this.state.activity.ledger.length)) ? (
            <p>
              {this.state.status.is_ready ? (
                <RaisedButton
                  label="Manage bank account & review past payouts"
                  primary={false}
                  href={makeStripeDestination()}
                />
              ) : (
                <StartRevenueSharingButton
                  label="Connect bank account to receive payouts"
                  onSubmit={country => {
                    window.location.href = makeStripeDestination(country);
                  }}
                />
              )}
            </p>
          ) : this.state.isFetchingActivity ? (
            <WorkingIndicator size={0.75} style={{}} />
          ) : (
            <p style={{ fontStyle: "italic" }}>
              Once you ({(UserStore.getUser() || {}).organization}) have been
              invited to Revenue Sharing for at least one event, you can add
              your bank account or debit card details here.
            </p>
          )}
        </div>
        <div style={{ marginTop: 40 }}>
          <h3>Your Revenue Sharing Activity</h3>
          {this.state.activity ? (
            <div>
              <TableRefreshIndicator
                isRefreshing={this.state.isFetchingActivity}
                onRefresh={() => this._fetchActivity()}
                style={{ marginTop: -50 }}
              />
              <h4 style={{ marginTop: 20 }}>Currently connected events</h4>
              <div style={{ fontSize: 13, marginTop: 10 }}>
                {this.state.activity.current_events.length ? (
                  <GenericAnalyticsTable
                    csvLabel="eventive-events"
                    noSort
                    noDownload
                    data={this.state.activity.current_events.map(event => {
                      return {
                        organization_name: event.organization_name,
                        name: event.name,
                        user_percent: formatPercent(event.user_percent),
                        user_donation_percent: formatPercent(
                          event.user_donation_percent
                        ),
                        actions: {
                          value: "",
                          label: (
                            <span>
                              <a target="_blank" href={event.public_url}>
                                View&nbsp;live
                              </a>
                              {event.can_view_event_data ? (
                                <span>
                                  {" "}
                                  &middot;{" "}
                                  <Link to={`/revenue_sharing/${event.id}`}>
                                    View&nbsp;analytics
                                  </Link>
                                </span>
                              ) : null}
                            </span>
                          )
                        }
                      };
                    })}
                    columns={[
                      { label: "Organization", value: "organization_name" },
                      { label: "Name", value: "name" },
                      { label: "Net %", value: "user_percent" },
                      {
                        label: "Donation %",
                        value: "user_donation_percent"
                      },
                      { label: "Actions", value: "actions" }
                    ]}
                    itemsPerPage={5}
                  />
                ) : (
                  <div
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "1em",
                      padding: 10,
                      fontWeight: "bold",
                      textAlign: "center"
                    }}
                  >
                    You have not been invited to Revenue Sharing for any current
                    events. If you're expecting to see something here, please
                    make sure you've accepted the email invitation, or{" "}
                    <LinkLike
                      onTouchTap={() =>
                        Intercom(
                          "showNewMessage",
                          `I need help with Eventive Revenue Sharing: `
                        )
                      }
                    >
                      send us a message
                    </LinkLike>{" "}
                    if you need help.
                  </div>
                )}{" "}
              </div>

              <h4
                style={{ marginTop: 30, display: "flex", alignItems: "center" }}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  Revenue Sharing {isCurrentMonth ? "upcoming" : ""} payout for
                </span>
                <div
                  style={{
                    display: "inline-block",
                    width: 250,
                    marginLeft: 10
                  }}
                >
                  <MonthPicker
                    value={this.state.selectedMonth}
                    onChange={month => {
                      this.setState({ selectedMonth: month }, () =>
                        this._fetchActivity()
                      );
                    }}
                  />
                </div>
              </h4>
              {!this.state.isFetchingActivity && (
                <p style={{ fontSize: 13, marginTop: 10, fontStyle: "italic" }}>
                  {isCurrentMonth
                    ? `The month is ongoing, and your payout may change. `
                    : `The below is your total payout for the selected month. `}
                  Revenue Sharing totals shown below are computed after
                  ticketing fees and refunds have been deducted. A 1% Stripe
                  service fee will be deducted from the final payout.
                </p>
              )}
              <div style={{ fontSize: 13, marginTop: 10 }}>
                {this.state.isFetchingActivity ? (
                  <WorkingIndicator centered style={{}} />
                ) : this.state.activity.ledger.length ? (
                  <div>
                    <div style={{ marginTop: -5, marginBottom: 15 }}>
                      Looking for more details?{" "}
                      <LinkLike onTouchTap={() => this.exportReport()}>
                        Download itemized report for{" "}
                        {moment(this.state.selectedMonth, "MM-YYYY").format(
                          "MMMM YYYY"
                        )}
                        .
                      </LinkLike>
                    </div>
                    <GenericAnalyticsTable
                      csvLabel="eventive-revenue-sharing"
                      csvDetail={this.state.selectedMonth}
                      noSort
                      noDownload
                      data={this.state.activity.ledger.map(item => {
                        return {
                          name: item.label,
                          net: {
                            value: item.net,
                            label: formatPrice(item.net, {
                              currency: item.currency
                            })
                          }
                        };
                      })}
                      columns={[
                        {
                          label: `Payout for ${
                            (UserStore.getUser() || {}).organization
                          }`,
                          value: "name"
                        },
                        {
                          label: `${Object.entries(
                            this.state.activity.ledger.reduce((totals, l) => {
                              if (!totals[l.currency]) {
                                totals[l.currency] = 0;
                              }
                              totals[l.currency] += l.net;
                              return totals;
                            }, {})
                          )
                            .map(
                              ([currency, value]) =>
                                `${formatPrice(value, { currency: currency })}`
                            )
                            .join(" + ")}`,
                          value: "net"
                        }
                      ]}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "1em",
                      padding: 10,
                      fontWeight: "bold",
                      textAlign: "center"
                    }}
                  >
                    Your payout for the selected period is estimated to be
                    $0.00.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <WorkingIndicator centered style={{ marginTop: 20 }} />
          )}
        </div>
      </div>
    );
  }
}

export default connectToStores(RevenueSharing);



// WEBPACK FOOTER //
// ./src/app/components/revenue-sharing.jsx